import React, { useRef, useState, useEffect } from "react";
import { Link, useLocation, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogIn, Mail, Lock, Eye, EyeOff } from "lucide-react";
import useAuth from "../../hooks/useAuth";
import Input from "../../components/forms/Input";
import Button from "../../components/forms/Button";
import axios from "../../api/axios";
import ABSLogoDark from "../../assets/ABS-Logo/darkTheme/ABS-Dark.png";
import ABSLogoLight from "../../assets/ABS-Logo/lightTheme/ABS-Light.png";

const LOGIN_URL = "/api/auth/login";

const Login = () => {
  const { auth, setAuth, persist, setPersist } = useAuth();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/main/dashboard";

  const userRef = useRef();

  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Detect dark mode
  const [isDarkMode, setIsDarkMode] = useState(
    document.documentElement.classList.contains("dark"),
  );

  // ALL useEffect hooks must be here, before any early return
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.documentElement.classList.contains("dark"));
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setErrMsg("");
  }, [email, pwd]);

  useEffect(() => {
    localStorage.setItem("persist", persist);
  }, [persist]);

  // Already authenticated — go to dashboard instead of showing login
  const existingToken =
    auth?.accessToken || localStorage.getItem("accessToken");
  if (existingToken) {
    return <Navigate to="/main/dashboard" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(
        LOGIN_URL,
        JSON.stringify({ email, password: pwd, rememberMe: persist }),
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        },
      );

      const accessToken = response?.data?.accessToken;
      const roles = response?.data?.roles;
      const employee = response?.data?.employee;
      const isFirstLogin = response?.data?.isFirstLogin;

      console.log("[Login] Response received:", {
        accessToken: !!accessToken,
        roles,
        isFirstLogin,
        persist,
      });

      // Always store token in localStorage for the session
      if (accessToken) {
        localStorage.setItem("accessToken", accessToken);
        console.log("[Login] Token stored in localStorage");

        // Store auth object for persist
        const authData = {
          accessToken,
          roles,
          employee,
          isFirstLogin,
          email: employee?.email,
        };

        // Always store auth data for current session (even if persist is false)
        // This allows navigation to work properly
        localStorage.setItem("auth", JSON.stringify(authData));
        console.log("[Login] Auth data stored (persist:", persist, ")");

        // Set auth state
        setAuth(authData);

        console.log("[Login] Auth state set:", {
          hasToken: !!authData.accessToken,
        });

        setEmail("");
        setPwd("");

        // Determine target path
        const targetPath = isFirstLogin ? "/change-password" : from;
        console.log("[Login] Navigating to:", targetPath);

        // Use window.location for more reliable navigation after login
        // This ensures clean state and avoids race conditions with React Router
        window.location.href = targetPath;
        return;
      }
    } catch (err) {
      if (!err?.response) {
        setErrMsg("No Server Response");
      } else if (err.response?.status === 400) {
        setErrMsg(err.response?.data?.message || "Missing Email or Password");
      } else if (err.response?.status === 401) {
        setErrMsg(err.response?.data?.message || "Invalid email or password");
      } else {
        setErrMsg(err.response?.data?.message || "Login Failed");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const togglePersist = () => {
    setPersist((prev) => !prev);
  };

  const currentLogo = isDarkMode ? ABSLogoDark : ABSLogoLight;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-primary-bg flex items-center justify-center p-4 transition-colors duration-200">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary-accent/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-orange-600/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
      </div>

      {/* Login Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md relative z-10"
      >
        <div className="card shadow-2xl">
          {/* Logo */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.3 }}
            className="flex justify-center mb-8"
          >
            <img
              src={currentLogo}
              alt="ABS Logo"
              className="h-12 w-auto object-contain"
            />
          </motion.div>

          {/* Title */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-center mb-8"
          >
            <h1 className="text-3xl font-bold text-gray-900 dark:text-light-100 mb-2">
              Welcome Back
            </h1>
            <p className="text-gray-600 dark:text-light-500">
              Sign in to continue to your dashboard
            </p>
          </motion.div>

          {/* Error Message */}
          {errMsg && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-100 dark:bg-red-900/20 border border-red-400 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-xl mb-6"
            >
              <p className="text-sm font-medium">{errMsg}</p>
            </motion.div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email Field */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Input
                label="Email"
                type="email"
                placeholder="Enter your email"
                icon={<Mail className="w-5 h-5" />}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                ref={userRef}
              />
            </motion.div>

            {/* Password Field */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
            >
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-gray-700 dark:text-light-200">
                  Password
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-light-500">
                    <Lock className="w-5 h-5" />
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    required
                    autoComplete="current-password"
                    className="input-field w-full pl-10 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-light-500 hover:text-primary-accent dark:hover:text-primary-accent transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>
            </motion.div>

            {/* Remember Me & Forgot Password */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="flex items-center justify-between"
            >
              <label
                className="flex items-center gap-2 cursor-pointer group"
                title="Keep you signed in for 30 days"
              >
                <input
                  type="checkbox"
                  checked={persist}
                  onChange={togglePersist}
                  className="w-4 h-4 text-primary-accent bg-gray-100 dark:bg-dark-400 border-gray-300 dark:border-dark-100 rounded focus:ring-2 focus:ring-primary-accent cursor-pointer"
                />
                <span className="text-sm text-gray-700 dark:text-light-300 group-hover:text-primary-accent transition-colors">
                  Remember me
                </span>
              </label>
              <Link
                to="/forgot-password"
                className="text-sm text-primary-accent hover:text-primary-accent-hover transition-colors font-medium"
              >
                Forgot password?
              </Link>
            </motion.div>

            {/* Submit Button */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <Button
                type="submit"
                variant="primary"
                className="w-full"
                icon={<LogIn className="w-5 h-5" />}
                disabled={isLoading}
              >
                {isLoading ? "Signing in..." : "Sign In"}
              </Button>
            </motion.div>
          </form>

          {/* Sign Up Link */}
        </div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-6 text-center text-sm text-gray-500 dark:text-light-500"
        >
          &copy; 2026 ABS. All rights reserved.
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Login;
